import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Navigation, Keyboard, Mousewheel } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, Checkbox,
    Grid,
    IconButton,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    ListItemText,
    MenuItem,
    Tab,
    Tabs,
} from '@mui/material';
import { ChevronLeft, Delete, DriveFileMove, Rotate90DegreesCcw, Rotate90DegreesCw } from '@mui/icons-material';
import { a11yProps } from 'components/mui/Tabs';
import useContextMenu from 'hooks/useContextMenu';
import { CarModel } from 'models/StockModel';
import useCarPhotos from 'modules/cars/hooks/useCarPhotos';
import auth from 'recoil/atoms/auth';
import { reloadImg, removeItemAtIndex } from 'utils/common';


type ViewPhotosDialogProps = {
    car: CarModel,
    startAlbum: string,
}

const albums = [
    'yard_note',
    'yard_cargo',
    'yard_problems',
    'vessel_note',
    'destination_note',
    'destination_problems',
];

const AlbumDialog = ({car, startAlbum}: ViewPhotosDialogProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const [urls, setUrls] = useState<Record<string, string[]>>(() =>
        car.albums.filter(a => a.count)
            .reduce((o, a) => {
                o[a.name] = [];
                return o;
            }, {} as Record<string, string[]>)
    );
    const [album, setAlbum] = useState(startAlbum);
    const [image, setImage] = useState(-1);
    const [selectionModel, setSelectionModel] = useState<Record<number, boolean>>({});
    const photos = useCarPhotos();
    const navigate = useNavigate();

    const {openMenu: openMove} = useContextMenu<string>(
        ({value, close}) => albums.map(album => (
            <MenuItem
                key={album}
                onClick={() => {
                    move(value, album);
                    close();
                }}
            >
                <ListItemText>{album.replace('_note', '').replace('_', ' ')}</ListItemText>
            </MenuItem>
        ))
    );

    useEffect(() => {
        if (!urls[album].length)
            photos.getPhotos(car.id, album)
                .then(data => setUrls(old => ({...old, [album]: data.urls})));
        // eslint-disable-next-line
    }, [car.id, album]);

    const move = (p: string, a: string) => photos.move(p, a)
        .then(data => {
            setSelectionModel({});

            if (a === album)
                return;

            const idx = urls[album].findIndex(u => u.includes(p));

            setUrls(old => {
                let temp = {
                    ...old,
                    [album]: removeItemAtIndex(old[album], idx),
                }

                if (!temp[album].length) {
                    delete temp[album];

                    setAlbum(a);
                }

                return {
                    ...temp,
                    [a]: [data.url, ...(old[a] || [])],
                }
            });
        });

    const remove = (indexes: number[]) => setUrls(old => {
        if (old[album].length <= indexes.length) {
            const {[album]: _, ...rest} = old;

            const keys = Object.keys(rest);
            if (keys.length)
                setAlbum(keys[0]);
            else
                navigate(-1);

            return rest;
        }

        console.log(indexes);

        return {
            ...old,
            [album]: old[album].filter((_, idx) => !indexes.includes(idx)),
        }
    });

    const reload = (idx: number) => reloadImg(urls[album][idx]);

    const canManage = my.permissions.has('cars update photos');

    const hasSelected = Object.values(selectionModel).includes(true);

    const deleteSelected = () => {
        const indexes = (Object.keys(selectionModel) as unknown as number[])
            .filter(idx => selectionModel[idx]).map(Number);

        const paths = indexes.map(
            idx => urls[album][idx].split('?')[0].split('.com/')[1]
        );

        photos.confirmDeletePhoto(paths)
            .then(() => {
                setSelectionModel({});
                remove(indexes);
            });
    }

    return (
        <Box
            sx={{
                '.swiper-slide': {
                    display: 'flex',
                    justifyContent: 'center',

                    'img': {
                        maxHeight: '90vh',
                        maxWidth: '100%',
                        display: 'block',
                    },
                },
            }}
        >
            <Grid container alignItems="center">
                <Grid>
                    <IconButton
                        aria-label="back"
                        onClick={() => image > -1 ?
                            setImage(-1) :
                            navigate(-1)}
                    >
                        <ChevronLeft color="action"/>
                    </IconButton>
                </Grid>
                <Grid>
                    {image === -1 && (
                        <Tabs
                            variant="scrollable"
                            value={album}
                            aria-label="albums"
                            onChange={(e, v) => setAlbum(v)}
                        >
                            {Object.keys(urls).map(tab => (
                                <Tab
                                    key={tab}
                                    value={tab}
                                    label={tab.replace('_note', '').replace('_', ' ')}
                                    {...a11yProps('albums', tab)}
                                />
                            ))}
                        </Tabs>
                    )}
                </Grid>
            </Grid>

            {urls[album] && (image > -1 ? (
                <Swiper
                    modules={[Navigation, Keyboard, Mousewheel]}
                    initialSlide={image}
                    navigation={!isMobile}
                    centeredSlides
                    loop
                    keyboard
                    mousewheel
                    style={{width: '100%'}}
                >
                    {urls[album].map((url, i) => (
                        <SwiperSlide key={i}>
                            <img
                                src={url}
                                alt=""
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            ) : (
                <ImageList cols={isMobile ? 2 : 6}>
                    {urls[album].map((url, i) => (
                        <ImageListItem
                            key={i}
                            sx={{
                                '& .MuiImageListItemBar-root': {
                                    opacity: {md: 0},
                                },
                                '&:hover, &.selected': {
                                    '& .MuiImageListItemBar-root': {
                                        opacity: {md: '100%'},
                                    },
                                },
                            }}
                            className={selectionModel[i] ? 'selected' : undefined}
                        >
                            <img
                                src={url}
                                alt=""
                                onClick={() => {
                                    setImage(i);
                                    setSelectionModel({});
                                }}
                            />
                            {canManage &&
                                <ImageListItemBar
                                    sx={{
                                        background:
                                            'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                                            'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                                    }}
                                    position="top"
                                    title={
                                        <Checkbox
                                            size="small"
                                            sx={{
                                                '&, &.Mui-checked': {
                                                    color: 'white',
                                                },
                                            }}
                                            onChange={e => setSelectionModel(old => ({...old, [i]: e.target.checked}))}
                                        />
                                    }
                                    actionIcon={
                                        <Actions
                                            url={url}
                                            openMove={openMove}
                                            rotate={(p, d) => photos.rotate(p, d).then(() => reload(i))}
                                            remove={p => photos.confirmDeletePhoto([p]).then(() => remove([i]))}
                                        />
                                    }
                                />
                            }
                        </ImageListItem>
                    ))}
                </ImageList>
            ))}

            {hasSelected && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: '1rem',
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Button
                        color="error"
                        variant="contained"
                        onClick={deleteSelected}
                    >
                        {t('delete_selected')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};


type ActionsProps = {
    url: string,
    openMove: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, v: string) => void,
    rotate: (p: string, d: string) => any,
    remove: (p: string) => any,
};

const Actions = ({url, remove, openMove, rotate}: ActionsProps) => {
    const filepath = url.split('?')[0]
        .split('.com/')[1];

    return (
        <>
            <IconButton
                sx={{color: 'white'}}
                aria-label="move photo"
                onClick={e => openMove(e, filepath)}
            >
                <DriveFileMove/>
            </IconButton>

            <IconButton
                sx={{color: 'white'}}
                aria-label="rotate photo left"
                onClick={() => rotate(filepath, 'left')}
            >
                <Rotate90DegreesCcw/>
            </IconButton>

            <IconButton
                sx={{color: 'white'}}
                aria-label="rotate photo right"
                onClick={() => rotate(filepath, 'right')}
            >
                <Rotate90DegreesCw/>
            </IconButton>

            <IconButton
                sx={{color: 'white'}}
                aria-label="delete photo"
                onClick={() => remove(filepath)}
            >
                <Delete/>
            </IconButton>
        </>
    )
};

export default AlbumDialog;
